import { Directive, inject, OnDestroy, output, viewChild } from "@angular/core";
import { AbstractControl, FormBuilder, FormGroup } from "@angular/forms";
import { SnackBarService } from "../snack-bar/services/snack-bar.service";
import { TranslateService } from "@ngx-translate/core";
import { CrudService } from "./crud-service.interface";
import { DialogComponent } from "../dialog/components/dialog.component";
import { Subscription } from "rxjs";
import { Router } from "@angular/router";

@Directive()
export abstract class CrudDialogBase<T extends { [K in keyof T]: AbstractControl<any, any>; }, Entity> implements OnDestroy {

    /** Dialog template reference */
    public dialog = viewChild.required<DialogComponent>(DialogComponent);

    /** Entity id reference */
    protected id: number | undefined = undefined;

    /** Speed trial result formgroup reference */
    protected formGroup: FormGroup<T> | undefined = undefined;

    /** Entity create success message */
    protected abstract successCreateKey: string;

    /** Entity create fail message */
    protected abstract failCreateKey: string;

    /** Entity update success message */
    protected abstract successUpdateKey: string;

    /** Entity update fail message */
    protected abstract failUpdateKey: string;

    /** Form builder reference */
    protected builder = inject(FormBuilder);

    /** Router reference */
    protected router = inject(Router);

    /** Service subscription reference */
    protected sub = new Subscription();

    /** Snackbar service reference */
    private snackBar = inject(SnackBarService);

    /** Translate service reference */
    private translate = inject(TranslateService);

    /** Output signal emitter reference */
    public reload = output<void>();

    constructor(protected service: CrudService<Entity>) {}
    
    ngOnDestroy(): void {
        /** unsubscribes from observables */
        this.sub.unsubscribe();
    }

    /**
     * Overridable method for obtaining correct data
     * @returns the correct data / formgroup value
     */
    protected getData(): unknown {
        return {...this.formGroup.getRawValue()};
    }

    /**
     * Overridable method if other parameter than id has to be set
     * @param id the received id
     */
    protected setId(id?: number): void {
        this.id = id;
    }

    /**
     * Overridable method for dialog cancel button click logic
     */
    protected afterCancelClick(): void {};

    /**
     * Overridable method for before dialog show logic
     */
    protected beforeDialogShow(data?: unknown): void {};

    /**
     * Method for after data prefill logic
     */
    protected abstract afterDataPrefill(data?: Entity);

    /**
     * Overridable method for after data save logic
     */
    protected afterDataSave(data?: unknown) {};

    /**
     * Shows dialog content
     * @param id the id of the displayed entity
     * @param data the additional data for dialog
     */
    public show(id?: number, data?: unknown): void {
        this.setId(id);
        this.beforeDialogShow(data);
        this.loadPrefilledData();
        this.dialog().show();
    }

    /**
     * Loads the prefilled data if entity exists
     */
    private loadPrefilledData(): void {
        /** if entity does not exist, do nothing */
        if (!this.id) {
            this.afterDataPrefill();
            return;
        }

        /** gets the existing entity to prefill dialog */
        this.sub.add(this.service.get(this.id).subscribe((result: Entity) => {
            this.afterDataPrefill(result);
        }));
    }

    /**
     * Executes the competition registration logic according to the button clicked
     * @param success the type of button clicked
     */
    protected onClickButton(success: boolean): void {
        /** if cancel button is clicked, just close the dialog */
        if (!success) {
        this.afterCancelClick();
        return;
        }

        if (!this.id) {
            /** if id is not provided, creates new competition registration */
            this.sub.add(
                this.service.create(this.getData()).subscribe((result) => {
                    if (result) {
                        /** shows success snackbar when crew is registered to competition */
                        this.snackBar.success(this.translate.instant(this.successCreateKey));

                        /** reloads registered crews table */
                        this.reload.emit();
                        this.afterDataSave(result);
                        return;
                    }

                    /** shows danger snackbar when crew registration to competition fails */
                    this.snackBar.danger(this.translate.instant(this.failCreateKey));
                })
            );
        } else {
            /** if id is provided, update existing competition registration */
            this.sub.add(
                this.service.update(this.getData()).subscribe((result) => {
                    if (result) {
                        /** shows success snackbar when competition registration is updated */
                        this.snackBar.success(this.translate.instant(this.successUpdateKey));

                        /** reloads registered crews table */
                        this.reload.emit();
                        this.afterDataSave(result);
                        return;
                    }

                    /** shows danger snackbar when update of competition registration fails */
                    this.snackBar.danger(this.translate.instant(this.failUpdateKey));
                })
            );
        }
    }
}